import React, { useState, useRef, useEffect, useMemo } from "react";

import { useSelector, useDispatch } from "react-redux";

import _ from "lodash";

import cx from "classnames";

import { Fonts } from "@paperflite-marketing/cleverstory-commons";
import { Button, Tab, Tabs } from "react-bootstrap";

import Tooltip from "components/Tooltip";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import EmptyMessage from "../../components/EmptyMessage";

import { 
  associateFontWithBrandkitButton,
  createBrandkitButton, 
  deleteBrandkitButtons, 
  deleteBrandkitNewButtons, 
  fetchBrandkitButtons, 
  invalidateBrandkitButton, 
  renameBrandkitButton, 
  updateBrandkitButtonContent, 
  updateBrandkitButtonProperty, 
  updateBrandkitButtons
} from "actions/brandKit/buttons";

import { BRANDKIT_BUTTON_COMPONENT } from "constants/brandKit/button";
import { getStyles } from "components/Editor/widgets/button";
import { getStylesForLayout } from "components/Editor/helper/layoutManager";
import { Property } from "components/Editor/components/properties";
import { Font } from "components/Editor/components/properties/fonts";
import DeleteModal from "../../components/DeleteModal";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import useToastMessage from "../../components/toastMessageHook";
import styles from '../brandkit.elements.module.scss';
import { MIXPANEL_BRANDKIT_CREATED } from "constants/mixpanelEvents";
import { createMixpanelEvent } from "actions/mixpanel";

function ButtonsElement() {

  const dispatch = useDispatch();
  const containerRef = useRef(null);

  const paletteIds = useSelector((state) => _.get(state, "brandKit.activated.items", []));
  const loading = useSelector(state => _.get(state, 'brandKit.buttons.loading'));
  const componentsList = useSelector(state => _.get(state, 'brandKit.buttons.components')) || [];
  const deleting = useSelector(state => _.get(state, 'brandKit.buttons.deleting', false));
  const updating = useSelector(state => _.get(state, 'brandKit.buttons.updating', false));
  const updated = useSelector(state => _.get(state, 'brandKit.buttons.updated', false));
  const error = useSelector(state => _.get(state, 'brandKit.buttons.error', false));
  

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([])
  const [toggleBrandElements, setToggleBrandElements] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [editMode, setEditMode] = useState({
    id: null,
    type: null
  });

  const paletteId = useMemo(
    () => _.find(paletteIds, { type: "BUTTON" })?.id,
    [paletteIds]
  );
  const enabled = useMemo(
    () => _.find(paletteIds, { type: "BUTTON" })?.enabled,
    [paletteIds]
  );

  useEffect(()=>{
    if (enabled != toggleBrandElements) {
      setToggleBrandElements(enabled)
    }
  },[enabled])

  useEffect(() => { 
    if(paletteId){
      dispatch(fetchBrandkitButtons(paletteId));
    }
  }, [paletteId, toggleBrandElements])

  useEffect(() => {
    if (!loading && showDeleteModal && !deleting) {
      setShowDeleteModal(false);
      setSelectedItems([]);
    }
  }, [deleting]);

  useEffect(() => {
    setSelectedItems([]);
  },[updating])

  useToastMessage(updated, invalidateBrandkitButton, error, 'Buttons');

  const deleteModalOpenHandler = () => {
    if (selectedItems?.length != 0) {
      setShowDeleteModal(true);
    };
  };

  const handleCreateButton = () => {
    dispatch(
      createBrandkitButton(BRANDKIT_BUTTON_COMPONENT())
    );
    setTimeout(() => {
      containerRef.current?.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }, 300);
  };

  const resetHandler = () => {
    if(paletteId){
      dispatch(fetchBrandkitButtons(paletteId));
    }
  };

  const saveChangesHandler = () => {
    const request = componentsList
      ?.filter(({ changes }) => changes)
      .map(({ id, name, newComponent, draft, order }) => ({
        name,
        ...(newComponent ? {} : { id }),
        order,
        widgets: draft?.widgets || [],
      }));
  
    dispatch(updateBrandkitButtons(paletteId, request));

    var mixpanelPayload = {
      brandkit_element_type: "Buttons"
    }

    dispatch(createMixpanelEvent(MIXPANEL_BRANDKIT_CREATED, mixpanelPayload));
  };

  const handleDelete = () => {
    const ids = selectedItems
    .filter(item => !item.newComponent)
    .map(item => item.id);

    if (ids.length > 0) {
      dispatch(deleteBrandkitButtons(ids, paletteId))
    }

    const newComponentsIds = selectedItems
    .filter(item => item.newComponent)
    .map(item => item.id);

    if (newComponentsIds.length > 0) {
      dispatch(deleteBrandkitNewButtons(newComponentsIds))
      if (!deleting) {
        setTimeout(() => {
          setShowDeleteModal(false);
          setSelectedItems([]);
        }, 500);
      }
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={cx(styles.secondaryContainer, styles.container)}>
        <div className={styles.mainContainer}>
          <Header
            selectedCategory={"Buttons"}
            toggleBrandElements={toggleBrandElements}
            setToggleBrandElements={setToggleBrandElements}
            selectedItems={selectedItems}
            deleteHandler={deleteModalOpenHandler}
            loading={loading}
            paletteId={paletteId}
          >
            <Button 
              variant="primary" 
              onClick={handleCreateButton} 
              className={styles.uploadButton}
              style={{backgroundColor: isHovered ? "var(--primaryAppColor)" : "transparent"}}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <i className="cleverstory-plus" />
              {"Create Button"}
            </Button>
          </Header>

          <div className={styles.mainContentWrapper} ref={containerRef}>
            <div
              className={styles.mainButtonContainer}
              style={{
                height:
                (loading && componentsList?.length === 0)
                    ? "100%"
                    : (!loading && componentsList?.length === 0)
                    ? "100%"
                    : "max-content"
              }}
            >
              {(loading && componentsList?.length === 0 )? (
              <div className={styles.loaderContainer}>
                <label>Just a moon-ute... Scanning for buttons... </label>
              </div>
              ) : (!loading && componentsList?.length === 0) ? (
                <EmptyMessage />
              ) : (
                componentsList?.map((data) => (
                  <ButtonPalatte
                    key={data.id}
                    paletteId={paletteId}
                    component={data}
                    deleting={deleting}
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    editMode={editMode}
                    setEditMode={setEditMode}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <DeleteModal
          showDeleteModal={true}
          message={`Are you sure you want to delete ${
            selectedItems.length > 1
              ? `these ${selectedItems.length} files`
              : "this file"
          }?`}
          title={`Delete Buttons`}
          isDeleting={deleting}
          backdrop={deleting}
          cancelHandler={() => setShowDeleteModal(false)}
          handleDeleteClick={handleDelete}
        />
      )}

      <Footer
        resetHandler={resetHandler}
        updating={updating}
        saveChangesHandler={saveChangesHandler}
        saveDisable={updating || componentsList?.length === 0}
      />
    </div>
  );
}

function ButtonPalatte({ deleting, paletteId, component, selectedItems, setSelectedItems, editMode, setEditMode }) {
  const { id, name } = component || {};

  const widget = component?.draft?.widgets?.[0] || {};
  const { properties } = widget || {};
  const layout = _.get(properties,`desktop.layout`, {});

  const dispatch = useDispatch();

  const multiple = true;
  const [show, setShow] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [showTextButton, setShowTextButton] = useState(false);
  const [renameEnabled, setRenameEnabled] = useState(false);
  const [showDeleteModal, setDeleteModal] = useState(false);
  const [headerName, setHeaderName] = useState(name);

  const buttonEditRef = useRef(null);
  const buttonTextEditRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const input = inputRef?.current;
    if (!input) return;
    input.value = name;
    setHeaderName(name);
  }, [name]);

  useEffect(() => {
    let input = inputRef?.current;
    if (!input) return;
    if (renameEnabled) {
      input.focus();
    }

    const blurHandler = () => {
      setRenameEnabled(false);
      renameHandler(id, input.value);
      setHeaderName(input.value);
    };

    const keyHandler = (event) => {
      if (event.key === "Enter") {
        blurHandler();
      }
    };

    input?.addEventListener("blur", blurHandler);
    input?.addEventListener("keydown", keyHandler);
    return () => {
      input?.removeEventListener("blur", blurHandler);
      input?.removeEventListener("keydown", keyHandler);
    };
  }, [renameEnabled]);

  useEffect(() => {
    if (editMode?.id === id) {
      switch (editMode?.type) {
        case 'customize':
          setShowButton(true);
          setShowTextButton(false);
          setShow(false);
          break;
        case 'edit_text':
          setShowButton(false);
          setShowTextButton(true);
          setShow(false);
          break;
        case 'options':
          setShowButton(false);
          setShowTextButton(false);
          setShow(true);
          break;
        default:
          break;
      }
    } else {
      setShowButton(false);
      setShowTextButton(false);
      setShow(false);
    }
  }, [editMode]);
  

  const handleSelect = (component) => {
    var updated = [...selectedItems];

    if (multiple) {
      const exists = updated.find(m => m.id === component.id) != null;
      if (exists) {
        updated = updated.filter(m => m.id !== component.id);
      } else {
        updated = [...updated, component];
      }
    } else {
      updated = [component];
    }
    
    setSelectedItems(updated)
  }

  const handleDelete = () => {

    if (component.newComponent) {
      dispatch(deleteBrandkitNewButtons([id]))

      if (!deleting) {
        setTimeout(() => {
          setDeleteModal(false);
          setSelectedItems([]);
        }, 500);
      }
    } else {
      dispatch(deleteBrandkitButtons([id], paletteId))
    }
  };

  const handleCustomize = (e) => {
    e.stopPropagation();
    setEditMode({
      id: id,
      type: 'customize'
    })
    setShowTextButton(false);
  }

  const handleEditText = (e) => {
    e.stopPropagation();
    setEditMode({
      id: id,
      type: 'edit_text'
    })
    setShowButton(false);
    setShow(false);
  }

  const handleOptions = (e) => {
    e.stopPropagation();
    setEditMode({
      id: id,
      type: 'options'
    })
  }

  const renameHandler = (id, name) => {
    dispatch(renameBrandkitButton(id, widget?.uid, name));
  };

  const widgetStyle = useMemo(
    () => getStylesForLayout(layout, "desktop"),
    [layout]
  );

  return (
    <div
      className={cx(
        styles.componentStyleWrapper, 
        (selectedItems?.some(data => data.id === id)) && styles.componentStyleWrapperSelected
      )}
      style={{ marginTop: "0px" }}
      onClick={() => handleSelect(component)}
    >
      <div className={styles.newStyleHeaderWrapper}>
        <div>
          {!renameEnabled ?
            <TruncatedTextWithTooltip            
              content={headerName} 
              maxWidth={200}
            />
            :
            <input
              ref={inputRef}
              value={headerName}
              onChange={(e) => setHeaderName(e.target.value)}
              className={styles.widgetHeadingName}
              disabled={!renameEnabled}
            />
          }
        </div>
        <Button
          variant="secondary"
          ref={buttonTextEditRef}
          id={`brandkitTextButton_${id}`}
          className={styles.btnIcons}
          style={{ marginLeft: "auto" }}
          onClick={(e) => handleEditText(e)}
        >
          <i className={cx('cleverstory-text-layer', styles.textLayerIcon)} />
        </Button>
        <Button
          variant="secondary"
          ref={buttonEditRef}
          id={`brandkitButton_${id}`}
          className={styles.btnIcons}
          onClick={(e) => handleCustomize(e)}
        >
          <i className={"cleverstory-customize"} />
        </Button>
        {showButton && (
          <ButtonStyleEditor
            id={id}
            ref={buttonEditRef}
            widget={widget}
            position={"center"}
            headerName={headerName}
            onCancel={() => setShowButton(false)}
          />
        )}
        {showTextButton && (
          <ButtonTextEditor
            id={id}
            ref={buttonTextEditRef}
            widget={widget}
            position={"center"}
            headerName={headerName}
            onCancel={() => setShowTextButton(false)}
          />
        )}
        <div className={styles.moreOptionsWrapper}>
          <Button
            variant="secondary"
            className={styles.btnIcons}
            onClick={(e) => handleOptions(e)}
          >
            <i className={"cleverstory-more"} />
          </Button>
          {show && (
            <div
              className={styles.moreOptionsList}
              onClick={(e) => e.stopPropagation()}
            >
              <span
                onClick={() => {
                  setRenameEnabled(!renameEnabled);
                  setShow(false);
                  inputRef?.current?.focus();
                }}
              >
                Rename
              </span>
              <span
                onClick={() => {
                  setShow(false);
                  setDeleteModal(true);
                }}
              >
                Delete
              </span>
            </div>
          )}
          {showDeleteModal && (
            <DeleteModal
              showDeleteModal={showDeleteModal}
              message={`Are you sure you want to delete ${headerName}?`}
              title={`Delete ${headerName}`}
              isDeleting={deleting}
              backdrop={deleting}
              cancelHandler={() => setDeleteModal(false)}
              handleDeleteClick={handleDelete}
            />
          )}
        </div>
      </div>
      <div className={styles.buttonWrapper}>
        <ButtonWidgetComponent
          className="brandkitButton"
          style={widgetStyle}
          widgetProps={widget}
          headerName={headerName}
        />
      </div>
    </div>
  );
}

const ButtonWidgetComponent = React.forwardRef(
  ({ className, style, widgetProps, headerName }, widgetRef) => {
    const [state, setState] = useState({
      active: "regular",
      buttonStyles: {},
    });

    const id = _.get(widgetProps, "uid");
    const type = _.get(widgetProps, "type");
    const properties = _.get(widgetProps, "properties", {});
    const content = _.get(widgetProps, 'content');

    Fonts.useFont(_.get(widgetProps, "fonts") || []);

    useEffect(() => {
      var buttonStyles = getStyles(properties, "desktop", state.active);
    
      setState((prevState) => {
        const updatedStyles = {
          ...buttonStyles,
          width: style["width"],
          height: style["height"],
        };
    
        if (JSON.stringify(prevState.buttonStyles) !== JSON.stringify(updatedStyles)) {
          return {
            ...prevState,
            buttonStyles: updatedStyles,
          };
        }
        return prevState;
      });
    }, [properties, style, state.active]);
    

    const handleOnMouseOver = () => {
      setState((state) => {
        return {
          ...state,
          active: "hover",
        };
      });
    };

    const handleOnMouseOut = () => {
      setState((state) => {
        return {
          ...state,
          active: "regular",
        };
      });
    };

    return (
      <div
        id={id}
        ref={widgetRef}
        className={cx(className, styles.button)}
        style={style}
        data-widget-id={id}
        data-widget-type={type}
        data-selectable={true}
        onMouseOver={handleOnMouseOver}
        onMouseOut={handleOnMouseOut}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.inner} style={state.buttonStyles}>
          {content}
        </div>
      </div>
    );
  }
);

const ButtonStyleEditor = React.forwardRef(
  ({ id, widget, position, headerName, ...rest }) => {
    const dispatch = useDispatch();

    const breakpoint = "desktop";

    const properties = _.get(widget, `properties.${breakpoint}`, {});

    const alignment = _.get(properties, "alignment", {
      position: "center",
      margin: 5,
    });

    const [state, setState] = useState({
      activeTab: "regular",
    });

    const regularShadowProps = _.get(
      widget,
      `properties.${breakpoint}.regular.shadow`, {}
    );
    const hoverShadowProps = _.get(
      widget,
      `properties.${breakpoint}.hover.shadow`, {}
    );

    const handleTabChange = (key) => {
      setState(() => {
        return {
          ...state,
          activeTab: key,
        };
      });
    };

    const handlePropertyChange = (value, fontId) => {

        const properties = {
          desktop: value,
          mobile: value
        };

        dispatch(
          updateBrandkitButtonProperty(id, widget.uid, properties)
        );

        if(fontId) {
          dispatch(associateFontWithBrandkitButton(id, widget.uid, [fontId]))
        }
    };

    const handleAlignmentChange = (value) => {
      const position = _.get(value, "position");

      if (position === "center") {
        value = {
          ...value,
          margin: 5,
        };
      }

      var request = {
        ...properties,
        alignment: {
          ...alignment,
          ...value,
        },
      };

      handlePropertyChange(request);
    };

    const handleShadowChange = (field, value) => {
      var request = {
        ...properties,
        [state.activeTab]: {
          ...properties[state.activeTab],
          shadow: {
            ...properties[state.activeTab].shadow,
            [field]: value,
          },
        },
      };
      handlePropertyChange(request);
    };

    return (
      <Property.Dialog
        className={styles.fontDialog}
        width={246}
        target={`#${widget.uid}`}
        title={"Customize Button"}
        onCancel={rest.onCancel}
        brandKit={true}
      >
        <Property.Body onClick={(e) => e.stopPropagation()} brandkitButton={true} >
          <Property
            name="Text Alignment"
            className={styles.buttonAlignmentProperty}
          >
            <ButtonAlignment
              value={alignment}
              onChange={handleAlignmentChange}
            />
          </Property>
          <Property
            name="Button Design"
            className={styles.buttonDesignProperty}
          >
            <Tabs
              activeKey={state.activeTab}
              onSelect={handleTabChange}
              className={styles.buttonStyleTabs}
            >
              <Tab
                eventKey="regular"
                title="Regular"
                className={styles.buttonStyleTab}
              >
                <FontEditor
                  id={id}
                  properties={properties}
                  active={"regular"}
                  handlePropertyChange={handlePropertyChange}
                />
                <DropShadow
                  properties={regularShadowProps}
                  handleShadowChange={handleShadowChange}
                />
              </Tab>
              <Tab
                eventKey="hover"
                title="Hover"
                className={styles.buttonStyleTab}
              >
                <FontEditor
                  id={id}
                  properties={properties}
                  active={"hover"}
                  handlePropertyChange={handlePropertyChange}
                />
                <DropShadow
                  id={id}
                  properties={hoverShadowProps}
                  handleShadowChange={handleShadowChange}
                />
              </Tab>
            </Tabs>
          </Property>
        </Property.Body>
      </Property.Dialog>
    );
  }
);

const ButtonTextEditor = (props) => {
  const dispatch = useDispatch();

  const [error, setError] = useState(null);

  const id = _.get(props, 'id');
  const widget_uid = _.get(props, 'widget.uid');
  const value = _.get(props, 'widget.content');

  const handleChange = (e) => {
    var value = e.target.value;

    if (!value || value.trim().length === 0) {
      setError("Please enter button text")
    } else {
      setError(null);
    }

    dispatch(updateBrandkitButtonContent(id, widget_uid, value));
  }

  return (
    <Property.Dialog
      width={222}
      target={`#${widget_uid}`}
      title='Change Button Text'
      onCancel={props.onCancel}
    >
      <Property name='What do you want to call this Button?'>
        <Property.Input value={value}
          onChange={handleChange}
          placeholder="Button Text"
          error={error} />
      </Property>
    </Property.Dialog>
  );
}

const ButtonAlignment = ({ value, onChange }) => {
  const position = _.get(value, "position") || "center";
  const margin = _.get(value, "margin", 0);

  const handleAlignmentChange = (position) => {
    var request = {
      position: position,
    };

    onChange(request);
  };

  const handleMarginChange = (margin) => {
    var request = {
      margin: margin,
    };

    onChange(request);
  };

  return (
    <div className={styles.buttonAlignment}>
      <div className={styles.options}>
        <Tooltip placement="top" content="Left Align">
          <button
            className={cx(styles.alignment, {
              [styles.active]: position === "left",
            })}
            onClick={() => handleAlignmentChange("left")}
          >
            <i className={cx("cleverstory-align-left")}></i>
          </button>
        </Tooltip>
        <Tooltip placement="top" content="Center Align">
          <button
            className={cx(styles.alignment, {
              [styles.active]: position === "center",
            })}
            onClick={() => handleAlignmentChange("center")}
          >
            <i className={cx("cleverstory-align-center")}></i>
          </button>
        </Tooltip>
        <Tooltip placement="top" content="Right Align">
          <button
            className={cx(styles.alignment, {
              [styles.active]: position === "right",
            })}
            onClick={() => handleAlignmentChange("right")}
          >
            <i className={cx("cleverstory-align-right")}></i>
          </button>
        </Tooltip>
      </div>

      {position !== "center" && (
        <Property name="Margin" className={styles.marginProperty}>
          <Property.Slider
            min={0}
            max={100}
            step={1}
            value={margin}
            onChange={handleMarginChange}
          />
        </Property>
      )}
    </div>
  );
};

const FontEditor = ({ properties, active, handlePropertyChange }) => {
  const deviceProperties = _.get(properties, `${active}`, {});
  const border = _.get(deviceProperties, "border", {});
  const borderWidth = _.get(border, "width", 0);
  const borderRadius = _.get(border, "radius", {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0,
  });

  const fontFamily = _.get(deviceProperties, "font.fontFamily", "Montserrat,sans-serif");
  const fontSize = _.get(deviceProperties, "font.fontSize") || 16;
  const lineSpacing = _.get(deviceProperties, "font.lineSpacing") || 1.2;
  const letterSpacing = _.get(deviceProperties, "font.letterSpacing") || 0;
  const fontStyle = _.get(deviceProperties, "font.style", {});

  const [borderColor, setBorderColor] = useState(_.get(border, "color", null));
  const [textColor, setTextColor] = useState(
    _.get(deviceProperties, "font.color", null)
  );
  const [backgroundColor, setBackgroundColor] = useState(
    _.get(deviceProperties, "background", null)
  );

  const handleBorderColor = (value) => {
    setBorderColor(value);
  };
  const handleTextColor = (value) => {
    setTextColor(value);
  };
  const handleBackgroundColor = (value) => {
    setBackgroundColor(value);
  };

  useEffect(() => {
    if (!_.isEqual(borderColor, _.get(border, `color`))) {
      handleBorderColorChange(borderColor);
      return;
    }
    if (!_.isEqual(textColor, _.get(deviceProperties, "font.color"))) {
      handleTextColorChange(textColor);
      return;
    }
    if (!_.isEqual(backgroundColor, _.get(deviceProperties, "background"))) {
      handleBackgroundColorChange(backgroundColor);
      return;
    }
  }, [borderColor, backgroundColor, textColor]);

  const handleTextColorChange = React.useCallback(
    (color) => {
      var request = {
        ...properties,
        [active]: {
          ...properties[active],
          font: {
            ...deviceProperties.font,
            color: color,
          },
        },
      };

      handlePropertyChange(request);
    },
    [properties]
  );

  const handleBackgroundColorChange = React.useCallback(
    (color) => {
      var request = {
        ...properties,
        [active]: {
          ...properties[active],
          background: color,
        },
      };

      handlePropertyChange(request);
    },
    [properties]
  );

  const handleBorderColorChange = (color) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        border: {
          ...border,
          color: color,
        },
      },
    };

    handlePropertyChange(request);
  };

  const handleBorderWidthChange = (value) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        border: {
          ...border,
          width: value,
        },
      },
    };

    handlePropertyChange(request);
  };

  const handleBorderRadiusChange = (radius) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        border: {
          ...border,
          radius: radius,
        },
      },
    };

    handlePropertyChange(request);
  };

  const handleFontFamily = (value) => {
    var fontId = _.get(value, "font.id");
    var family = _.get(value, "font.fontFamily");
    if (!family) {
      return;
    }

    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        font: {
          ...deviceProperties.font,
          fontFamily: family,
        },
      },
    };

    handlePropertyChange(request, fontId);
  };

  const handleSizeChange = (value) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        font: {
          ...deviceProperties.font,
          fontSize: value,
        },
      },
    };

    handlePropertyChange(request);
  };

  const handleLineSpacingChange = (value) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        font: {
          ...deviceProperties.font,
          lineSpacing: value,
        },
      },
    };

    handlePropertyChange(request);
  };

  const handleLetterSpacingChange = (value) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        font: {
          ...deviceProperties.font,
          letterSpacing: value,
        },
      },
    };

    handlePropertyChange(request);
  };

  const handleFontStyleChange = (style) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        font: {
          ...deviceProperties.font,
          style: {
            ...fontStyle,
            [style]: !fontStyle[style],
          },
        },
      },
    };

    handlePropertyChange(request);
  };

  return (
    <>
      <Property.Accordion title={"Color"} className={styles.colorProperty}>
        <Property.Color
          title={"Text"}
          value={_.get(deviceProperties, "font.color", null)}
          onChange={handleTextColorChange}
          brandkit={true}
        />
        <Property.Color
          title={"Background"}
          gradient={true}
          clearable={true}
          value={_.get(deviceProperties, "background", null)}
          onChange={handleBackgroundColorChange}
          brandkit={true}
        />
      </Property.Accordion>

      <Property.Accordion title={"Border"} className={styles.borderProperty}>
        <Property.Color
          title={"Color"}
          clearable={true}
          value={borderColor}
          onChange={handleBorderColor}
          brandkit={true}
        />
        {active === "regular" && (
          <>
            <Property
              name="Stroke Width"
              className={styles.buttonStyleProperty}
            >
              <Property.Slider
                min={0}
                max={10}
                step={1}
                input
                value={borderWidth}
                onChange={handleBorderWidthChange}
              />
            </Property>
            <Property
              name="Corner Radius"
              className={styles.buttonStyleProperty}
            >
              <Property.BorderRadius
                value={borderRadius}
                onChange={handleBorderRadiusChange}
              />
            </Property>
          </>
        )}
      </Property.Accordion>
      {active === "regular" && (
        <Property.Accordion title={"Text"} className={styles.textProperty}>
          <Font.Family
            value={fontFamily}
            onChange={handleFontFamily}
            className={styles.buttonStyleProperty}
          />
          <Font.Size
            value={fontSize}
            onChange={handleSizeChange}
            className={styles.buttonStyleProperty}
          />

          <Property className={styles.buttonStyleProperty}>
            <div className={styles.fontActions}>
              <Property.Action
                className={cx(styles.iconButton, {
                  [styles.active]: _.get(fontStyle, "bold", false),
                })}
                onClick={handleFontStyleChange.bind(this, "bold")}
                tooltip="Bold"
              >
                <i className={"cleverstory-bold"} />
              </Property.Action>
              <Property.Action
                className={cx(styles.iconButton, {
                  [styles.active]: _.get(fontStyle, "italic", false),
                })}
                onClick={handleFontStyleChange.bind(this, "italic")}
                tooltip="Italic"
              >
                <i className={"cleverstory-italics"} />
              </Property.Action>
              <Property.Action
                className={cx(styles.iconButton, {
                  [styles.active]: _.get(fontStyle, "underline", false),
                })}
                onClick={handleFontStyleChange.bind(this, "underline")}
                tooltip="Underline"
              >
                <i className={"cleverstory-underline"} />
              </Property.Action>
            </div>
          </Property>
          <Property name="Line Spacing" className={styles.lineSpacingProperty}>
            <Property.Slider
              input
              min={0.5}
              max={3.0}
              step={0.1}
              value={lineSpacing}
              onChange={handleLineSpacingChange}
            />
          </Property>
          <Property
            name="Character Spacing"
            className={styles.characterSpacingProperty}
          >
            <Property.Slider
              input
              min={-0.1}
              max={0.7}
              step={0.05}
              value={letterSpacing}
              onChange={handleLetterSpacingChange}
            ></Property.Slider>
          </Property>
        </Property.Accordion>
      )}
    </>
  );
};

const DropShadow = ({ properties, handleShadowChange }) => {
  const [shadowColor, setShadowColor] = useState(
    _.get(properties, `color`, { r: 255, g: 255, b: 255, a: 1 })
  );
  const show = _.get(properties, `show`);

  const handleShadowColor = (value) => {
    setShadowColor(value);
  };

  useEffect(() => {
    if (_.isEqual(shadowColor, _.get(properties, `color`))) {
      return;
    }
    handleShadowChange("color", shadowColor);
  }, [shadowColor]);

  return (
    <div className={styles.dropShadow}>
      <div className={styles.toggleShadow}>
        <div className={styles.shadowLable}>Drop Shadow</div>
        <Property.Switch
          value={_.get(properties, "show", false)}
          onChange={() =>
            handleShadowChange("show", !_.get(properties, "show", false))
          }
        />
      </div>

      {show && (
        <>
          <div className={styles.fields}>
            <Property.BoxShadow
              value={_.get(properties, "axes", { x: 0, y: 0, b: 0 })}
              onChange={(value) => handleShadowChange("axes", value)}
            />

            <Property.Color
              title="Shadow Color"
              className={styles.shadowColor}
              value={_.get(properties, "color", {
                r: 255,
                g: 255,
                b: 255,
                a: 1,
              })}
              onChange={handleShadowColor}
              brandkit={true}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ButtonsElement;